import React from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { TablePagination } from '@material-ui/core';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import queryString from 'query-string';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import Tooltip from '@material-ui/core/Tooltip';
import { RouteComponentProps } from 'react-router-dom';

import api from 'api';
import { OrgnizationInvitationsType } from 'store/modules/organizations/organizations';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import { useFormatMessage } from 'components/IntlProvider';

const { withRouter } = RouterProvider;

interface InvitationsProps
  extends Pick<OrgnizationInvitationsType, 'count' | 'list'> {
  currentPage: number;
  refresh: () => void;
  match: {
    params: {
      codename: string;
    };
  };
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: theme.spacing(3),
      overflowX: 'auto',
      border: '1px solid #CECECA',
      boxShadow: ' 0 4px 12px 0 rgba(0,0,0,0.1)',
      borderRadius: '10px',
    },
    table: {
      minWidth: 650,
    },
    action: {
      cursor: 'pointer',
    },
  })
);

const Invitations = ({
  count,
  list,
  history,
  currentPage,
  location: { pathname },
  match: {
    params: { codename },
  },
  refresh,
}: InvitationsProps & RouteComponentProps) => {
  const classes = useStyles();
  const t = useFormatMessage();
  const onPageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number
  ) => {
    const params = { limit: 20, offset: 20 * page };
    history.push(`${pathname}?${queryString.stringify(params)}`);
  };

  const removeInvitation = (key: string, organization_name: string) =>
    api.resources
      .removeInvitationOrganizationUser({ key, organization_name })
      .then(() => {
        refresh();
      });

  return (
    <Paper className={classes.root}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>
              {t('dashboard.organizations.invitations.email', 'Email')}
            </TableCell>
            <TableCell>
              {t('dashboard.organizations.invitations.used', 'Used')}
            </TableCell>
            <TableCell>
              {t('dashboard.organizations.invitations.sent', 'Sent')}
            </TableCell>
            <TableCell align="right">
              {t('dashboard.organizations.users.actions', 'Actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((invitation) => (
            <TableRow hover key={invitation.id}>
              <TableCell component="th" scope="row">
                {invitation.id}
              </TableCell>
              <TableCell>{invitation.email}</TableCell>
              <TableCell>
                {invitation.used ? <DoneIcon /> : <ClearIcon />}
              </TableCell>
              <TableCell>
                {invitation.sent ? <DoneIcon /> : <ClearIcon />}
              </TableCell>
              <TableCell align="right">
                <span
                  className={classes.action}
                  onClick={() => removeInvitation(invitation.key, codename)}
                >
                  <Tooltip
                    title={t('tooltip.invitations.delete', 'Delete invitation')}
                    placement="top"
                  >
                    <DeleteForeverIcon />
                  </Tooltip>
                </span>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TablePagination
              count={count}
              page={currentPage}
              rowsPerPage={20}
              onPageChange={onPageChange}
              rowsPerPageOptions={[20]}
            />
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  );
};

export default withRouter(Invitations);
